import {Icon} from '@therms/atalaya'
import {
  DateTime,
  DistanceHumanReadable,
  RPCDocs,
  UserName,
} from '@therms/react-modules'
import {
  MdAccessTime,
  MdOutlinePerson,
  MdPerson,
  MdPersonPin,
} from 'react-icons/md'
import dayjs from 'dayjs'

interface LocationActivitySummaryProps {
  activitySummary: RPCDocs.Locations.GetLocationActivitySummary.v1.ActivitySummary
}

export function LocationActivitySummary({
  activitySummary,
}: LocationActivitySummaryProps) {
  return (
    <div className="text-sm">
      {/* Last Activity Log Entry */}
      {!!activitySummary.lastActivityLogEntry && (
        <div className="mb-base gap-x-xs flex">
          <Icon
            Icon={MdAccessTime}
            size="sm"
            className="text-color-subtle mt-xxs shrink-0"
          />

          <div>
            <div className="text-color-subtle text-xs font-medium">
              Last Log Entry
            </div>
            <div>
              <span className="font-medium">
                <DateTime
                  relative
                  timestamp={dayjs()
                    .subtract(
                      activitySummary.lastActivityLogEntry.minutesAgo,
                      'minute',
                    )
                    .toISOString()}
                />
              </span>
              <span className="text-color-subtle text-xs">
                {' '}
                ({activitySummary.lastActivityLogEntry.createdBy.displayName})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* On-Site Users */}
      {!!activitySummary.onSiteUsers?.length && (
        <div className="mb-base gap-x-xs flex">
          <Icon
            Icon={MdPerson}
            size="sm"
            className="text-color-subtle mt-xxs shrink-0"
          />

          <div>
            <div className="text-color-subtle text-xs font-medium">On-Site</div>
            <div>
              <span className="font-medium">
                {activitySummary.onSiteUsers.map((user, index) => (
                  <span key={user.id}>
                    {user.displayName}
                    {!!user.status && (
                      <span className="text-color-subtle text-xs">
                        {' '}
                        ({user.status.title})
                      </span>
                    )}
                    {activitySummary.onSiteUsers.length > 1 &&
                      index !== activitySummary.onSiteUsers.length - 1 &&
                      ', '}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Last On-Site User (only show if no current on-site users) */}
      {!activitySummary.onSiteUsers?.length &&
        !!activitySummary.lastOnSiteUser && (
          <div className="mb-base gap-x-xs flex">
            <Icon
              Icon={MdOutlinePerson}
              size="sm"
              className="text-color-subtle mt-xxs shrink-0"
            />

            <div>
              <div className="text-color-subtle text-xs font-medium">
                Last On-Site
              </div>
              <div>
                <span className="font-medium">
                  <UserName user={activitySummary.lastOnSiteUser} />
                </span>
                <span className="text-color-subtle text-xs">
                  {' '}
                  (
                  <DateTime
                    relative
                    timestamp={dayjs()
                      .subtract(
                        activitySummary.lastOnSiteUser.minutesAgo,
                        'minute',
                      )
                      .toISOString()}
                  />
                  )
                </span>
              </div>
            </div>
          </div>
        )}

      {/* Nearby Users */}
      {!!activitySummary.nearbyUsers?.length && (
        <div className="mb-base gap-x-xs flex">
          <Icon
            Icon={MdPersonPin}
            size="sm"
            className="text-color-subtle mt-xxs shrink-0"
          />

          <div>
            <div className="text-color-subtle text-xs font-medium">Nearby</div>
            <div className="space-y-xxs">
              {activitySummary.nearbyUsers.map((user) => (
                <div key={user.id}>
                  <span className="font-medium">{user.displayName}</span>
                  <span className="text-color-subtle text-xs">
                    {' '}
                    (
                    <DateTime
                      relative
                      timestamp={dayjs()
                        .subtract(user.minutesAgo, 'minute')
                        .toISOString()}
                    />
                    ,{' '}
                    <DistanceHumanReadable
                      distanceMeters={user.distanceMeters}
                    />{' '}
                    away
                    {user.status && <>, {user.status.title}</>})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
